import type { EnumFormatConfig } from './column-format-options';
import { tasStateLabelMap } from 'tio-data/data/resources/tas-application.constants';

// Re-export for consumers that need the map directly
export { tasStateLabelMap };

// Program instance labels use descriptive forms to avoid ambiguity in flat
// report/filter contexts where multiple states map to "Pending" or "Approved".
export const TAS_PROGRAM_INSTANCE_STATE_LABELS: Record<string, string> = {
  'TAS.ProgramInstanceState.CREATED': 'Created',
  'TAS.ProgramInstanceState.DRAFT': 'In Draft',
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': 'Pending Program Approval',
  'TAS.ProgramInstanceState.PROGRAM_APPROVED': 'Program Approved',
  'TAS.ProgramInstanceState.PROGRAM_REJECTED': 'Program Rejected',
  'TAS.ProgramInstanceState.PROGRAM_NOT_APPROVED': 'Program Not Approved',
  'TAS.ProgramInstanceState.PRIMED': 'In Progress',
  'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': 'Pending Completion Approval',
  'TAS.ProgramInstanceState.COMPLETION_APPROVED': 'Completion Approved',
  'TAS.ProgramInstanceState.COMPLETION_REJECTED': 'Completion Rejected',
  'TAS.ProgramInstanceState.COMPLETION_NOT_APPROVED': 'Completion Not Approved',
  'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': 'Pending Withdrawal Approval',
  'TAS.ProgramInstanceState.WITHDRAWAL_APPROVED': 'Withdrawal Approved',
  'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED': 'Withdrawal Rejected',
  'TAS.ProgramInstanceState.WITHDRAWAL_NOT_APPROVED': 'Withdrawal Not Approved',
  'TAS.ProgramInstanceState.COMPLETED': 'Completed',
  'TAS.ProgramInstanceState.WITHDRAWN': 'Withdrawn',
  'TAS.ProgramInstanceState.ABANDONED': 'Canceled',
};

// TAS audit datasets (audit_queue worklist + audit_log measurements).
export const AUDIT_DECISION_ACTION_LABELS: Record<string, string> = {
  approve_evidence: 'Approve evidence',
  reject_evidence: 'Reject evidence',
  decline_evidence: 'Decline evidence',
};

export const AUDIT_REASON_LABELS: Record<string, string> = {
  manual: 'Manual',
  high_amount: 'High amount',
  random_sample: 'Random sample',
  eval_disagreement: 'Eval disagreement',
  prior_audit: 'Prior audit',
};

export const AUDIT_OUTCOME_LABELS: Record<string, string> = {
  agreed: 'Agreed',
  corrected: 'Corrected',
};

export const AUDIT_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  escalated: 'Escalated',
  closed: 'Closed',
  superseded: 'Superseded',
};

export const AUDIT_FLAG_LABELS: Record<string, string> = {
  pending: 'Pending',
  audit: 'Flagged for audit',
  no_audit_needed: 'Not sampled',
  excluded: 'Excluded',
};

// Covers both decided_by (system/person) and audited_group (ai/human) tokens.
export const AUDIT_DECIDER_LABELS: Record<string, string> = {
  system: 'AI',
  person: 'Human',
  ai: 'AI',
  human: 'Human',
};

const ENUM_FIELD_REGISTRY: Record<string, Record<string, string>> = {
  // snake_case (fallback fields / OS source paths)
  tas_application_state_keyword: tasStateLabelMap,
  application_state: tasStateLabelMap,
  tas_application_payment_state_keyword: tasStateLabelMap,
  tas_program_instance_state_keyword: TAS_PROGRAM_INSTANCE_STATE_LABELS,
  action_taken_keyword: AUDIT_DECISION_ACTION_LABELS,
  correct_action_keyword: AUDIT_DECISION_ACTION_LABELS,
  audit_reason_keyword: AUDIT_REASON_LABELS,
  audit_outcome_keyword: AUDIT_OUTCOME_LABELS,
  audit_status_keyword: AUDIT_STATUS_LABELS,
  audit_flag_keyword: AUDIT_FLAG_LABELS,
  decided_by_keyword: AUDIT_DECIDER_LABELS,
  audited_group_keyword: AUDIT_DECIDER_LABELS,
  // camelCase (/fields API response)
  tasApplicationStateKeyword: tasStateLabelMap,
  applicationState: tasStateLabelMap,
  tasApplicationPaymentStateKeyword: tasStateLabelMap,
  tasProgramInstanceStateKeyword: TAS_PROGRAM_INSTANCE_STATE_LABELS,
  actionTakenKeyword: AUDIT_DECISION_ACTION_LABELS,
  correctActionKeyword: AUDIT_DECISION_ACTION_LABELS,
  auditReasonKeyword: AUDIT_REASON_LABELS,
  auditOutcomeKeyword: AUDIT_OUTCOME_LABELS,
  auditStatusKeyword: AUDIT_STATUS_LABELS,
  auditFlagKeyword: AUDIT_FLAG_LABELS,
  decidedByKeyword: AUDIT_DECIDER_LABELS,
  auditedGroupKeyword: AUDIT_DECIDER_LABELS,
};

export function getEnumFormatForField(fieldKey: string): EnumFormatConfig | undefined {
  const valueMap = ENUM_FIELD_REGISTRY[fieldKey];
  if (!valueMap) return undefined;
  return { type: 'enum', valueMap };
}

export function isEnumField(fieldKey: string): boolean {
  return fieldKey in ENUM_FIELD_REGISTRY;
}
